import type { Ref } from 'react';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { flip, shift, useFloating } from '@floating-ui/react';
import { ClickAwayListener } from '@mui/material';
import Popper from '@mui/material/Popper';
import PopupState, { bindToggle, bindPopper } from 'material-ui-popup-state';
import { ChevronRight } from 'react-feather';
import { PopperMenu, PopperMenuItem } from '@trinity/chat-components/components/Popper/index.js';
import { Portal } from '@trinity/chat-components/components/Portal/index.js';
import { MoreHorizontalAnchor } from '@/components/presentation/Anchor';
import { useProjectActions, useProjectActionsValidity } from '@/containers/GroupProject/hooks';
import { ProjectAction } from '@/enums';
import { useZIndexMiddleware } from '@trinity/chat-components/components/presentation/popper-modifiers.js';
import type { ProjectActionsContextMenuItem, ProjectActionsMenuProps, ProjectActionsPopperProps } from './interfaces';
import styles from './style/ProjectActionsContextMenu.module.css';

type ClickEvent = () => unknown;

const ProjectActionsContextMenu = forwardRef(({ excluded = [], onClose }: ProjectActionsMenuProps, ref: Ref<HTMLDivElement>) => {
  const actions = useProjectActions();
  const valid = useProjectActionsValidity();

  const [exportOpen, setExportOpen] = useState(false);

  const zIndexMiddleware = useZIndexMiddleware();
  const { refs, floatingStyles } = useFloating({
    middleware: [zIndexMiddleware, flip(), shift()],
    placement: 'right-start',
  });

  const handleClick = useCallback((fn: ClickEvent, action: ProjectAction) => () => {
    onClose(action);
    return fn();
  }, [onClose]);

  const move = useMemo(() => {
    return {
      children: 'Move',
      onClick: actions[ProjectAction.Move],
    };
  }, [actions]);

  const uploadQuantFile = useMemo(() => {
    return {
      children: 'Upload Quant Analysis File',
      onClick: actions[ProjectAction.UploadQuantAnalysisFile],
    };
  }, [actions]);

  const handleExportBlur = useCallback(() => {
    setExportOpen(false);
  }, []);

  const showExportItem = useMemo(() => ([
    // ProjectAction.DownloadCallReport,
    // ProjectAction.DownloadSurveyReport,
    ProjectAction.DownloadHighlightsReport,
    // ProjectAction.DownloadTagSummariesReport,
    ProjectAction.DownloadInsightsReport,
  ].filter(action => valid[action]).length > 0), [valid]);


  const items: Array<{ menuItem: ProjectActionsContextMenuItem; action: ProjectAction }> = useMemo(() => {

    const allMenuItemsMap: Partial<Record<ProjectAction, ProjectActionsContextMenuItem>> = {
      // [ProjectAction.Share]: share,
      // [ProjectAction.ChangeOwner]: ownership,
      // [ProjectAction.CreateCall]: createCall,
      // [ProjectAction.Clone]: cloneSurvey,
      // [ProjectAction.Archive]: archive,
      // [ProjectAction.Activate]: activate,
      [ProjectAction.Move]: move,
      [ProjectAction.UploadQuantAnalysisFile]: uploadQuantFile,
      // [ProjectAction.EditSurvey]: editSurvey,
      // [ProjectAction.ManageSurveySettings]: surveySettings,
      // [ProjectAction.Preview]: preview,
      // [ProjectAction.MonitorSurvey]: surveyMonitoring,
      // [ProjectAction.ManageDisplayrDashboard]: manageDisplayrDashboard,
      // [ProjectAction.ManageSelectDashboard]: manageSelectDashboard,
    };

    return Object.keys(allMenuItemsMap).map(key => key as ProjectAction)
      .filter(f => valid[f] && !excluded.includes(f))
      .map(m => ({ menuItem: allMenuItemsMap[m], action: m }));

  }, [move, uploadQuantFile, excluded, valid]);

  return (
    <>
      <PopperMenu ref={ref}>
        {items.map((item, i) => (
          <PopperMenuItem
            key={i}
            onClick={handleClick(item.menuItem.onClick, item.action)}>
            {item.menuItem.children}
          </PopperMenuItem>
        ))}
        {showExportItem &&
          <PopperMenuItem
            ref={refs.setReference}
            className={styles.exportItem}

            onClick={() => setExportOpen(true)}>
            Export
            <ChevronRight className={styles.icon} />
          </PopperMenuItem>
        }
      </PopperMenu>
      {exportOpen &&
        <Portal>
          <ClickAwayListener
            mouseEvent='onMouseDown'
            onClickAway={handleExportBlur}
            touchEvent='onTouchStart'>
            <div
              className={styles.menu}
              ref={refs.setFloating}
              style={floatingStyles}>
              <ExportActionsMenu excluded={excluded} onClose={handleExportBlur} />
            </div>
          </ClickAwayListener>
        </Portal>
      }
    </>
  );
});

const ExportActionsMenu = forwardRef(({ excluded = [], onClose }: ProjectActionsMenuProps, ref: Ref<HTMLDivElement>) => {

  const actions = useProjectActions();
  const valid = useProjectActionsValidity();

  const handleClick = useCallback((fn: ClickEvent) => () => {
    onClose();
    return fn();
  }, [onClose]);

  const highlightsReport = useMemo(() => ({
    children: 'Highlights',
    onClick: actions[ProjectAction.DownloadHighlightsReport],
  }), [actions]);

  // const tagSummariesExport = useMemo(() => ({
  //   children: 'Tag Summaries',
  //   onClick: actions[ProjectAction.DownloadTagSummariesReport],
  // }), [actions]);

  // const responseGridExport = useMemo(() => ({
  //   children: 'Response Grid',
  //   onClick: actions[ProjectAction.DownloadResponseGrid],
  // }), [actions]);

  // Leaving the code in for now, but team has decided for the time being to not include the Insights Report in MSL Copilot
  const insightsReport = useMemo(() => ({
    children: 'Insights Report',
    onClick: actions[ProjectAction.DownloadInsightsReport],
  }), [actions]);

  // const callReport = useMemo(() => {
  //   return {
  //     children: 'Call Data',
  //     onClick: actions[ProjectAction.DownloadCallReport],
  //   };
  // }, [actions]);

  // const surveyReport = useMemo(() => {
  //   return {
  //     children: 'Survey Data',
  //     onClick: actions[ProjectAction.DownloadSurveyReport],
  //   };
  // }, [actions]);

  const items: ProjectActionsContextMenuItem[] = useMemo(() => [
    // valid[ProjectAction.DownloadCallReport] && !excluded.includes(ProjectAction.DownloadCallReport) && callReport,
    // valid[ProjectAction.DownloadSurveyReport] && !excluded.includes(ProjectAction.DownloadSurveyReport) && surveyReport,
    valid[ProjectAction.DownloadHighlightsReport] && !excluded.includes(ProjectAction.DownloadHighlightsReport) && highlightsReport,
    // valid[ProjectAction.DownloadTagSummariesReport] && !excluded.includes(ProjectAction.DownloadTagSummariesReport) && tagSummariesExport,
    // valid[ProjectAction.DownloadResponseGrid] && !excluded.includes(ProjectAction.DownloadResponseGrid) && responseGridExport,
    // valid[ProjectAction.DownloadInsightsReport] && insightsReport,
  ].filter(Boolean), [excluded, highlightsReport, valid]);

  return (
    <PopperMenu ref={ref}>
      {items.map((item, i) => (
        <PopperMenuItem
          key={i}
          onClick={handleClick(item.onClick)}>
          {item.children}
        </PopperMenuItem>
      ))}
    </PopperMenu>
  );
});

export const ProjectActionsContextMenuPopper = ({
  Anchor = MoreHorizontalAnchor,
  className,
  excluded,
  placement = 'bottom-end',
  toggleUploadModal,
}: ProjectActionsPopperProps & { toggleUploadModal?: () => void }) => {

  // Override the action if toggleUploadModal is provided
  const handleMenuClose = (action?: ProjectAction) => {
    if (action === ProjectAction.UploadQuantAnalysisFile && toggleUploadModal) {
      toggleUploadModal();
    }
  };

  return (
    <PopupState variant="popper" popupId="project-actions-popper">
      {popupState => (
        <div className={className}>
          <div {...bindToggle(popupState)}>
            <Anchor open={popupState.isOpen} />
          </div>
          <Popper
            {...bindPopper(popupState)}
            placement={placement}
            className={styles.popper}>
            <ClickAwayListener
              onClickAway={() => popupState.close()}>
              <ProjectActionsContextMenu
                onClose={(action) => {
                  popupState.close();
                  handleMenuClose(action);
                }}
                excluded={excluded} />
            </ClickAwayListener>
          </Popper>
        </div>
      )}
    </PopupState>
  );
};